"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, CircleAlert, Loader2 } from "lucide-react";
import { ProductCardGrid } from "./product-card-grid";
import type { ChatProduct } from "./product-card";

interface MessagePartsProps {
  parts: { type: string; [key: string]: unknown }[];
}

/**
 * Typography for the assistant's markdown, at the canvas `body` role (14px / 20px).
 *
 * It replaces the five `prose*` classes this container used to carry, which were **inert**:
 * `@tailwindcss/typography` is in no package.json in the monorepo, no stylesheet registers it, and
 * `.prose` is emitted by no rule in help-web's compiled bundle — so the markdown rendered at UA
 * defaults, not at the canvas role. Installing the plugin was rejected (it would change every app's
 * emitted CSS for one component's markdown); these are explicit arbitrary-descendant variants, the
 * repo's own idiom (`ui/button.tsx`, `ui/badge.tsx`).
 *
 * **It deliberately sets no colour and no fill.** `MessageParts` renders inside *both* bubbles, and
 * help-web maps user content into a `text` part too, so anything here also lands on the user bubble,
 * whose ground is `bg-primary`: a link painted `text-primary` there would be `--primary` on
 * `--primary`, and a code fill of `bg-background` would be white on white. The colour layer therefore
 * lives on the assistant branch of `message-bubble.tsx` (`MARKDOWN_ASSISTANT_COLORS`), scoped to the
 * bare marker class `chat-md` below so it reaches markdown output and nothing else in the bubble.
 * The underline stays here on purpose — it is the non-colour cue that keeps a link identifiable in
 * the user bubble, where colour is inherited.
 *
 * Blocks the canvas does not spec — headings, tables, blockquotes, rules, images — stay at UA
 * defaults by design.
 */
const MARKDOWN_PROSE =
  "chat-md text-sm leading-5 " +
  "[&>*:first-child]:mt-0 [&_p]:my-0 [&_p+p]:mt-3 " +
  "[&_strong]:font-semibold [&_em]:italic " +
  "[&_a]:underline [&_a]:underline-offset-2 " +
  "[&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5 " +
  "[&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-5 " +
  "[&_li]:mt-1 " +
  "[&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs " +
  "[&_pre]:mt-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:p-3 [&_pre]:text-xs " +
  "[&_pre_code]:bg-transparent [&_pre_code]:p-0";

function findProducts(part: Record<string, unknown>): ChatProduct[] | null {
  // Deep-search the part object for a products array
  const search = (obj: unknown): ChatProduct[] | null => {
    if (!obj || typeof obj !== "object") return null;
    if (Array.isArray(obj)) {
      // Check if this array looks like products
      if (obj.length > 0 && typeof obj[0] === "object" && obj[0] !== null && "name" in obj[0] && "price" in obj[0]) {
        return obj as ChatProduct[];
      }
      return null;
    }
    const record = obj as Record<string, unknown>;
    if (Array.isArray(record.products)) {
      return record.products as ChatProduct[];
    }
    // Search nested properties
    for (const value of Object.values(record)) {
      const found = search(value);
      if (found) return found;
    }
    return null;
  };

  return search(part);
}

export function isToolRelated(part: Record<string, unknown>): boolean {
  const t = part.type as string;
  return t === "dynamic-tool" || t === "tool-invocation" || t.startsWith("tool-");
}

export function isToolLoading(part: Record<string, unknown>): boolean {
  if (!isToolRelated(part)) return false;
  // If we can find products, it's not loading
  if (findProducts(part)) return false;
  // Check various state indicators
  const state = part.state as string | undefined;
  if (state === "output-available" || state === "result") return false;
  return true;
}

/** Whether any text part carries visible reply text — whitespace (a streamed leading newline) is not. */
export function hasReplyText(parts: { type: string; [key: string]: unknown }[]): boolean {
  return parts.some((p) => p.type === "text" && typeof p.text === "string" && p.text.trim().length > 0);
}

/**
 * A finished tool call whose result is an error. help-api sanitizes every error-shaped result to
 * `{ error: "This tool is temporarily unavailable." }` before it reaches the browser, so such a result
 * carries no `products` array and must not read as an empty catalog.
 */
function isToolError(part: Record<string, unknown>): boolean {
  const output = (part.output ?? part.result) as unknown;
  return typeof output === "object" && output !== null && "error" in output;
}

/**
 * Tool-call row labels, keyed by the help-api tool name (`ChatToolRegistry`). A `null` entry is a tool
 * the visitor never sees: `logEvent` is analytics bookkeeping, not work done on their behalf.
 */
const TOOL_LABELS: Record<string, { pending: string; done: string; failed: string } | null> = {
  searchProducts: { pending: "Searching products", done: "Searched products", failed: "Couldn't search products" },
  getProductDetails: {
    pending: "Looking up product details",
    done: "Looked up product details",
    failed: "Couldn't look up product details",
  },
  compareProducts: { pending: "Comparing products", done: "Compared products", failed: "Couldn't compare products" },
  searchContent: {
    pending: "Searching help articles",
    done: "Searched help articles",
    failed: "Couldn't search help articles",
  },
  logEvent: null,
};

/** Whether a tool part carries a non-empty product list (and so renders the card grid). */
export function partHasProducts(part: Record<string, unknown>): boolean {
  return (findProducts(part)?.length ?? 0) > 0;
}

/** Whether a tool part renders anything at all — false only for the hidden tools above. */
export function isVisibleToolPart(part: Record<string, unknown>): boolean {
  return isToolRelated(part) && TOOL_LABELS[part.toolName as string] !== null;
}

/**
 * The compact tool-call row, shown whenever a visible tool call has no products to render (a call
 * that returns products renders the card grid instead, whose heading is its completion state). It
 * stays put once the call completes — pending shows a spinner, done a check, failed an alert — so a
 * search that finds nothing leaves a visible "no matches" record instead of collapsing to nothing.
 */
function ToolCallRow({ part }: { part: Record<string, unknown> }) {
  const loading = isToolLoading(part);
  const failed = !loading && isToolError(part);
  const toolName = part.toolName as string | undefined;
  const labels = (toolName && TOOL_LABELS[toolName]) || { pending: "Working", done: "Done", failed: "Failed" };
  // Only a real, empty product list is "no matches"; an error result carries no list at all.
  const noMatches = !loading && !failed && toolName === "searchProducts" && findProducts(part)?.length === 0;

  return (
    <div
      className="flex items-center gap-2 text-sm text-muted-foreground [&:not(:first-child)]:mt-3"
      // A live region on every state, not only while pending: removing the role as the call settles
      // tears the region down at the moment its text changes, so "no matches" / "Couldn't …" would
      // never be announced.
      role="status"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
      ) : failed ? (
        <CircleAlert className="h-4 w-4" aria-hidden />
      ) : (
        <Check className="h-4 w-4" aria-hidden />
      )}
      <span>
        {loading ? `${labels.pending}…` : failed ? labels.failed : labels.done}
        {noMatches ? " · no matches" : null}
      </span>
    </div>
  );
}

export function MessageParts({ parts }: MessagePartsProps) {
  return (
    <>
      {parts.map((part, index) => {
        if (part.type === "text") {
          const text = part.text as string;
          if (!text?.trim()) return null;
          return (
            <div key={index} className={MARKDOWN_PROSE}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {text}
              </ReactMarkdown>
            </div>
          );
        }

        // A tool part with products renders the card grid; any other visible one a tool-call row
        if (isVisibleToolPart(part)) {
          const products = findProducts(part);
          if (products && products.length > 0) {
            return <ProductCardGrid key={index} products={products} />;
          }
          return <ToolCallRow key={index} part={part} />;
        }

        // Skip step-start and other internal parts
        return null;
      })}
    </>
  );
}
